/**
 * Site-timezone-aware date formatting helpers.
 *
 * Every date in The Amino Exchange renders in the canonical site timezone
 * (default America/Chicago — Central Time). Users in any browser locale see
 * the same wall-clock reading, so a mod in Central Time and a member in
 * London are looking at the same "10:00 AM" on a shared shot / event.
 *
 * Usage: `useSiteTime()` from a React component to get {tz, tzShort, fmt*}.
 * Non-React code can call the raw helpers with `tz` passed explicitly.
 */

import { useSettings } from "../context/SettingsContext";
import { useAuth } from "../context/AuthContext";

export const DEFAULT_SITE_TZ = "America/Chicago";

/** Robust Date coercion — returns a Date or null. */
function _toDate(v) {
  if (v == null) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  try {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

/** Short abbreviation for a timezone at "now" (e.g. "CDT", "CST", "GMT"). */
export function tzShortLabel(tz) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "short",
    }).formatToParts(new Date());
    return parts.find((p) => p.type === "timeZoneName")?.value || "";
  } catch {
    return "";
  }
}

/** Full date+time in the site timezone. Falls back to em-dash on invalid. */
export function formatSiteDateTime(v, tz = DEFAULT_SITE_TZ, opts = {}) {
  const d = _toDate(v);
  if (!d) return "—";
  return new Intl.DateTimeFormat(undefined, {
    timeZone: tz,
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    ...opts,
  }).format(d);
}

/** Just the date portion, in the site tz. */
export function formatSiteDate(v, tz = DEFAULT_SITE_TZ) {
  const d = _toDate(v);
  if (!d) return "—";
  return new Intl.DateTimeFormat(undefined, {
    timeZone: tz,
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(d);
}

/** Just the time portion, in the site tz. */
export function formatSiteTime(v, tz = DEFAULT_SITE_TZ) {
  const d = _toDate(v);
  if (!d) return "—";
  return new Intl.DateTimeFormat(undefined, {
    timeZone: tz,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(d);
}

/** Short weekday name in the site tz (e.g. "Mon"). Useful for chart axes. */
export function formatSiteWeekday(v, tz = DEFAULT_SITE_TZ) {
  const d = _toDate(v);
  if (!d) return "—";
  return new Intl.DateTimeFormat(undefined, {
    timeZone: tz,
    weekday: "short",
  }).format(d);
}

/** React hook — pulls the current site tz from settings and returns bound formatters.
 * If the current user has flipped their `use_local_timezone` preference, dates
 * render in the browser's own IANA tz instead of the site's canonical one. */
export function useSiteTime() {
  const { settings } = useSettings();
  const { user } = useAuth() || {};
  const siteTz = (settings && settings.site_timezone) || DEFAULT_SITE_TZ;
  let tz = siteTz;
  let usingLocal = false;
  if (user && user.use_local_timezone) {
    try {
      const local = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (local) { tz = local; usingLocal = true; }
    } catch { /* fall back to site tz */ }
  }
  const short = tzShortLabel(tz);
  return {
    tz,
    tzShort: short,
    siteTz,
    usingLocal,
    fmtDateTime: (v, opts) => formatSiteDateTime(v, tz, opts),
    fmtDate: (v) => formatSiteDate(v, tz),
    fmtTime: (v) => formatSiteTime(v, tz),
    fmtWeekday: (v) => formatSiteWeekday(v, tz),
  };
}
